// CSR-1604 — Premium breakdown math, shared by the client (live Total as the
// CSR types) and the server (authoritative `premium` written on manual
// create/edit). One implementation so the two never drift.

export interface PremiumBreakdown {
  policyPremium?: string | number | null;
  brokerFee?: string | number | null;
  policyFees?: string | number | null;
  taxes?: string | number | null;
}

const PREMIUM_BREAKDOWN_FIELDS = [
  "policyPremium",
  "brokerFee",
  "policyFees",
  "taxes",
] as const;

// Largest value the `premium` / breakdown numeric(10,2) columns can hold.
// Each component is bounded to this, but four valid components can still sum
// past it, so the routes reject an over-large derived Total at the boundary.
export const MAX_PREMIUM = 99999999.99;

// CSR-1604/CSR-1605 — a breakdown component is a decimal money string up to the
// numeric(10,2) column: ≤8 integer digits + optional ≤2 decimals. Shared by the
// manual-edit Zod schema (rejects bad input as a 400) and the DC-read mapper
// (drops bad input to null). Unsigned: fees, taxes, commission %, and amount-due
// are never negative, and a negative one is suspicious, not legitimate.
export const MONEY_STRING_PATTERN = /^\d{1,8}(\.\d{0,2})?$/;

// CSR-1983 — the same shape with an optional leading minus, for the two values
// that CAN legitimately be negative: a policy's headline premium and its
// policyPremium after a record-update cancellation (recordUpdate.ts: "may be
// negative after a cancellation"). DC stores them signed.
export const MONEY_STRING_SIGNED_PATTERN = /^-?\d{1,8}(\.\d{0,2})?$/;

/**
 * Normalize a money input to a valid (unsigned) decimal string, or null when it
 * is blank or malformed. Invalid non-blank input (e.g. "N/A") returns null so it
 * never silently counts as 0 in computeTotalPremium, nor reaches the
 * numeric(10,2) column as a failing cast. Use {@link toSignedMoneyStringOrNull}
 * for the premium fields that may be negative after a cancellation.
 */
export function toMoneyStringOrNull(
  value: string | number | null | undefined,
): string | null {
  if (value === null || value === undefined || value === "") return null;
  const asString = String(value);
  return MONEY_STRING_PATTERN.test(asString) ? asString : null;
}

/**
 * Like {@link toMoneyStringOrNull} but accepts a leading "-". Only for the
 * policy premium / headline total, which a record-update cancellation drives
 * legitimately negative; DC stores and echoes the signed value, and the DC-read
 * mapper must accept it or it discards the whole breakdown as "untrusted"
 * (CSR-1983). "-" alone (no digits) is still malformed → null.
 */
export function toSignedMoneyStringOrNull(
  value: string | number | null | undefined,
): string | null {
  if (value === null || value === undefined || value === "") return null;
  const asString = String(value);
  return MONEY_STRING_SIGNED_PATTERN.test(asString) ? asString : null;
}

/** True when the derived Total overflows the numeric(10,2) premium column. */
export function exceedsPremiumLimit(total: string): boolean {
  return total !== "" && Number(total) > MAX_PREMIUM;
}

function hasMoneyValue(value: string | number | null | undefined): boolean {
  return value !== null && value !== undefined && value !== "";
}

function toCents(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

/**
 * Sum a list of money inputs in integer cents (to avoid floating-point drift on
 * `numeric(10,2)` values) and format back to a fixed 2-decimal string.
 * Blank/null/undefined and non-numeric garbage count as 0. Returns "" when every
 * value is blank, so an all-empty input never produces a spurious "0.00". The
 * result may be negative (a subtracted component can outweigh the rest).
 */
export function sumMoney(
  values: Array<string | number | null | undefined>,
): string {
  if (!values.some(hasMoneyValue)) return "";
  const cents = values.reduce<number>((sum, value) => sum + toCents(value), 0);
  return (cents / 100).toFixed(2);
}

/**
 * Total Premium = Policy Premium + Broker Fee + Finance Charge (`policyFees`) + Taxes.
 * Blank/null components count as 0. Returns "" when every component is blank, so
 * an all-empty breakdown never overwrites a value with "0.00".
 */
export function computeTotalPremium(breakdown: PremiumBreakdown): string {
  return sumMoney(PREMIUM_BREAKDOWN_FIELDS.map((field) => breakdown[field]));
}

/** True when at least one breakdown component carries a value. */
export function hasAnyBreakdown(breakdown: PremiumBreakdown): boolean {
  return PREMIUM_BREAKDOWN_FIELDS.some((field) => hasMoneyValue(breakdown[field]));
}
